import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useProgress = (courseId) => {
  const { user } = useAuth();
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && courseId) {
      fetchProgress();
    }
  }, [user, courseId]);

  const fetchProgress = async () => {
    try {
      const { data, error } = await supabase
        .from('progress')
        .select(`
          *,
          lessons (
            *,
            modules (course_id)
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      // Filter progress for the specific course
      const courseProgress = data?.filter(p => 
        p.lessons?.modules?.course_id === courseId
      ) || [];

      setProgress(courseProgress);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const markLessonComplete = async (lessonId) => {
    try {
      const { data, error } = await supabase
        .from('progress')
        .upsert([
          {
            user_id: user.id,
            lesson_id: lessonId,
            completed_at: new Date().toISOString()
          }
        ])
        .select()
        .single();

      if (error) throw error;

      await fetchProgress(); // Refresh progress
      toast.success('Lesson marked as complete!');
      return { data, error: null };
    } catch (error) {
      toast.error('Failed to update progress');
      return { data: null, error };
    }
  };

  const isLessonComplete = (lessonId) => {
    return progress.some(p => p.lesson_id === lessonId);
  };

  const getCourseProgress = (modules) => {
    const totalLessons = modules.reduce((acc, module) => 
      acc + (module.lessons?.length || 0), 0
    );
    
    const completedLessons = modules.reduce((acc, module) => {
      return acc + (module.lessons?.filter(lesson => 
        isLessonComplete(lesson.id)
      ).length || 0);
    }, 0);

    return {
      total: totalLessons,
      completed: completedLessons,
      percentage: totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0
    };
  };

  const getNextIncompleteLesson = (modules) => {
    for (const module of modules) {
      for (const lesson of module.lessons || []) {
        if (!isLessonComplete(lesson.id)) {
          return { lesson, module };
        }
      }
    }
    return null;
  };

  return {
    progress,
    loading,
    markLessonComplete,
    isLessonComplete,
    getCourseProgress,
    getNextIncompleteLesson,
    refetch: fetchProgress
  };
};